// PortRequestCard.jsx
//
// One row in the Port In tab list. Renders a port_request's status, number
// count, and last-updated time, plus a primary action (Continue for drafts,
// View for in-flight). The full detail view (PortRequestDetail with timeline)
// lands in Phase E.

import React, { useState } from 'react';
import {
  ChevronRight,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowLeftRight,
  Trash2,
  Loader2,
} from 'lucide-react';

const STATUS_META = {
  draft: {
    label: 'Draft',
    badgeClass: 'bg-slate-100 text-slate-700',
    icon: FileText,
    actionLabel: 'Continue editing',
  },
  awaiting_signature: {
    label: 'Awaiting signature',
    badgeClass: 'bg-amber-100 text-amber-800',
    icon: Clock,
    actionLabel: 'View',
  },
  awaiting_documents: {
    label: 'Awaiting documents',
    badgeClass: 'bg-amber-100 text-amber-800',
    icon: Clock,
    actionLabel: 'View',
  },
  ready_to_submit: {
    label: 'Ready to submit',
    badgeClass: 'bg-blue-100 text-blue-800',
    icon: ArrowLeftRight,
    actionLabel: 'View',
  },
  submitted: {
    label: 'Submitted',
    badgeClass: 'bg-blue-100 text-blue-800',
    icon: ArrowLeftRight,
    actionLabel: 'View',
  },
  in_carrier_review: {
    label: 'Carrier review',
    badgeClass: 'bg-indigo-100 text-indigo-800',
    icon: Clock,
    actionLabel: 'View',
  },
  scheduled: {
    label: 'Scheduled',
    badgeClass: 'bg-emerald-100 text-emerald-800',
    icon: Clock,
    actionLabel: 'View',
  },
  completed: {
    label: 'Completed',
    badgeClass: 'bg-emerald-100 text-emerald-800',
    icon: CheckCircle2,
    actionLabel: 'View',
  },
  exception: {
    label: 'Needs attention',
    badgeClass: 'bg-red-100 text-red-800',
    icon: AlertCircle,
    actionLabel: 'View',
  },
  canceled: {
    label: 'Canceled',
    badgeClass: 'bg-slate-100 text-slate-500',
    icon: XCircle,
    actionLabel: 'View',
  },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function formatForDisplay(e164) {
  if (!e164 || e164.length < 12) return e164;
  const d = e164.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return e164;
}

const TERMINAL_STATUSES = new Set(['completed', 'canceled']);

export default function PortRequestCard({ portRequest, onOpen, onDiscard }) {
  const status = portRequest.status ?? 'draft';
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  const numbers = Array.isArray(portRequest.numbers) ? portRequest.numbers : [];
  const numberCount = numbers.length;
  const firstNumber = numbers[0]?.phone_number;
  const isDraft = status === 'draft';
  const canDiscard = typeof onDiscard === 'function' && !TERMINAL_STATUSES.has(status);

  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState(null);

  const handleDiscardClick = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setDiscarding(true);
    setDiscardError(null);
    try {
      const err = await onDiscard(portRequest);
      if (err) {
        setDiscardError(err);
        setDiscarding(false);
      }
      // On success the parent removes this row from the list so we don't
      // need to clear local state.
    } catch (e) {
      setDiscardError(e?.message || 'Could not discard this port request.');
      setDiscarding(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 hover:border-orange-300 hover:shadow-sm rounded-xl transition-all">
      <div className="flex items-center gap-4 p-4">
        <button
          type="button"
          onClick={() => onOpen?.(portRequest)}
          className="flex-1 min-w-0 flex items-center gap-4 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${meta.badgeClass}`}>
                {meta.label}
              </span>
              <span className="text-xs text-slate-400">
                {numberCount === 0
                  ? 'No numbers yet'
                  : `${numberCount} number${numberCount === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="text-sm font-medium text-slate-900 truncate">
              {firstNumber
                ? `${formatForDisplay(firstNumber)}${numberCount > 1 ? ` and ${numberCount - 1} more` : ''}`
                : isDraft
                ? 'New draft, no numbers added yet'
                : 'Port request'}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Updated {formatDate(portRequest.updated_at) || formatDate(portRequest.created_at)}
            </div>
          </div>
        </button>

        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onOpen?.(portRequest)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            {meta.actionLabel}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Discard row. Renders below the main row so it can be inline-
          confirmed without the confirm UI fighting for space with the badge
          + number line. Only shown when the port is non-terminal and the
          parent wired onDiscard. */}
      {canDiscard && (
        <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-end gap-2">
          {discardError && (
            <span className="text-[11px] text-red-600 mr-auto flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {discardError}
            </span>
          )}
          {confirming && !discarding && (
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setDiscardError(null);
              }}
              className="text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1"
            >
              Keep
            </button>
          )}
          <button
            type="button"
            onClick={handleDiscardClick}
            disabled={discarding}
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${
              confirming
                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                : 'text-slate-500 hover:text-red-600'
            } disabled:opacity-50`}
          >
            {discarding ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Trash2 className="w-3 h-3" />
            )}
            {discarding ? 'Discarding' : confirming ? 'Yes, discard this port' : 'Discard'}
          </button>
        </div>
      )}
    </div>
  );
}
